/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvButton,
	EvIcon,
	EvInstructions,
	EvSection,
	EvTextInput,
	EvTitle,
	InfoStatusIcon,
	OptionType,
	TITLE_MODES,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { IWfaLabel } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { transformCoreLabelsToCustomLabels } from 'src/common/helpers/LabelDisplayHelpers';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useNotifications } from 'src/common/providers/notificationsProvider/NotificationsConsumer';
import { getFromLocalStorage, saveToLocalStorage } from 'src/common/services/api/LocalStorageApi';
import { Logger } from 'src/common/services/Logger';
import { ILabelConfig } from 'src/common/types/LabelsTypes';

import { PlatformPopup } from '../../../../common/components/platform-popup/PlatformPopup';
import { getSortedLabelsConfig } from '../../pages/sessions/store';
import { WFA_SESSIONS_LABEL_KEY_ORDER } from '../../pages/sessions/WfaSessionsPage.constants';
import { updateSession } from '../../services/wfa-session-update-service/WfaSessionUpdateService';

import {
	ARIA_LABEL_ID,
	DISMISS_MESSAGE,
	EDIT_SESSION_POPUP_SHOW_INFO_MESSAGE,
	INSTRUCTIONS
} from './consts';
import { isCoreLabel } from './EditSessionPopup.utils';
import { LabelsSelect } from './LabelsSelect';

import './EditSessionPopup.scss';

interface IEditSessionPopup {
	isOpen: boolean;
	sessionName: string;
	sessionId: string;
	labels: IWfaLabel[];
	onClose?: () => void;
	onSubmit?: (name?: string, labels?: IWfaLabel[]) => Promise<unknown>;
}

const transformOptions = (options: { key: string; name: string }[]): OptionType[] => {
	return options.map(({ key, name }) => ({
		label: name,
		value: key
	}));
};

export const EditSessionPopup: FC<IEditSessionPopup> = ({
	isOpen = false,
	sessionName,
	sessionId,
	labels,
	onClose,
	onSubmit
}) => {
	const notificationsContext = useNotifications();
	const { configuration } = useConfiguration();

	const [name, setName] = useState(sessionName);
	const [selectedLabels, setSelectedLabels] = useState(labels || []);
	const [showInfoMessage, setShowInfoMessage] = useState(
		getFromLocalStorage(EDIT_SESSION_POPUP_SHOW_INFO_MESSAGE) ?? true
	);

	const isLabelsToggleEnabled = (configuration.WFA?.TOGGLES as Record<string, boolean>)
		?.ENABLE_PLATFORM_LABELS as boolean;

	const labelsList = useMemo(
		() => [
			...(configuration?.PLATFORM?.ADDITIONAL_LABELS || []),
			...(configuration?.PLATFORM?.CORE_LABELS || [])
		],
		[configuration?.PLATFORM?.ADDITIONAL_LABELS, configuration?.PLATFORM?.CORE_LABELS]
	);
	const availableLabelsList: ILabelConfig[] = useMemo(
		() => transformCoreLabelsToCustomLabels(labelsList),
		[labelsList]
	);

	const sortedAvailableLabelsList = useMemo(
		() => getSortedLabelsConfig(availableLabelsList, WFA_SESSIONS_LABEL_KEY_ORDER),
		[availableLabelsList]
	);

	const dismissMessage = (): void => {
		saveToLocalStorage(EDIT_SESSION_POPUP_SHOW_INFO_MESSAGE, false);
		setShowInfoMessage(false);
	};

	const onConfirm = async (): Promise<void> => {
		try {
			await onSubmit?.(name, selectedLabels);
			await updateSession(sessionId, name, labels, selectedLabels);

			notificationsContext.toast({
				show: true,
				status: TOAST_STATUS.SUCCESS,
				announcement: 'Session updated successfully'
			});
		} catch (e) {
			Logger.error('Error updating session', e);
			notificationsContext.toast({
				show: true,
				status: TOAST_STATUS.FAIL,
				announcement: 'Failed to update session'
			});
		} finally {
			onClose();
		}
	};

	const onCancel = (): void => {
		onClose();
	};

	const handleChange = (labelConfig: ILabelConfig, newValue: OptionType | OptionType[]): void => {
		const newValuesNormalized = [].concat(newValue);

		setSelectedLabels((prev) => {
			const prevSelectedLabels = [...prev];

			const prevValuesWithoutTheChanged = prevSelectedLabels.filter(
				(prevValue) => prevValue.key !== labelConfig.key
			);

			if (newValuesNormalized.length) {
				const newLabel: IWfaLabel = {
					name: labelConfig.name,
					key: labelConfig.key,
					values: newValuesNormalized.map(({ label, value }) => ({ key: value, name: label }))
				};

				return [...prevValuesWithoutTheChanged, newLabel];
			}

			return prevValuesWithoutTheChanged;
		});
	};

	const buttons = [
		{
			onClick: onCancel,
			title: 'Cancel',
			type: BUTTON_TYPES.SECONDARY,
			children: 'Cancel'
		},
		{
			onClick: onConfirm,
			title: 'Save',
			type: BUTTON_TYPES.PRIMARY,
			children: 'Save'
		}
	];

	const renderEditSessionForm = (): JSX.Element => (
		<div className="edit-session-form">
			{showInfoMessage && (
				<div className="info-message-container">
					<div className="info-message">
						<EvIcon small icon={InfoStatusIcon} />
						<div>{DISMISS_MESSAGE}</div>
					</div>
					<EvButton type={BUTTON_TYPES.SECONDARY} className="dismiss-btn" onClick={dismissMessage}>
						Dismiss
					</EvButton>
				</div>
			)}
			<label id={ARIA_LABEL_ID}>Session name</label>
			<EvTextInput
				ariaLabelledBy={ARIA_LABEL_ID}
				value={name}
				className="field rename-session-input"
				onChange={setName}
				maxLength="255"
				isRequired
			/>

			{isLabelsToggleEnabled && (
				<EvSection>
					<EvTitle
						className="platform-labels-title"
						mode={TITLE_MODES.NORMAL}
						headingLevel={3}
						titleText="Platform labels"
						id="platform-labels"
					/>
					<EvInstructions isSmallText text={INSTRUCTIONS} />
					<div className="labels-container">
						{sortedAvailableLabelsList.map((item) => {
							const placeholder = `Select a ${item.name}`;
							const selectedLabel: IWfaLabel | undefined = selectedLabels?.find(
								(label) => label.key === item.key
							);
							let value: OptionType[];
							if (selectedLabel) {
								value = selectedLabel.values?.map((selectedValue) => ({
									label: selectedValue?.name,
									value: selectedValue?.key
								}));
							} else {
								value = [];
							}

							return (
								<LabelsSelect
									key={item.key}
									labelKey={item.key}
									options={transformOptions(item.options)}
									value={value}
									onChange={(selected) => handleChange(item, selected)}
									isMulti={!isCoreLabel(item.key)}
									description={item.description}
									placeholder={placeholder}
									ariaLabel={item.name}
								/>
							);
						})}
					</div>
				</EvSection>
			)}
		</div>
	);
	return (
		<PlatformPopup
			popupWidth={620}
			className="edit-session-popup"
			titleMode={TITLE_MODES.NORMAL}
			title="Edit Session"
			isOpen={isOpen}
			onClose={onClose}
			buttons={buttons}
			isCentered
			isControlled
			isBgTransparent
		>
			{renderEditSessionForm()}
		</PlatformPopup>
	);
};
